import {
  Check,
  CheckCircle,
  ChevronDown,
  Eye,
  Loader2,
  XCircle,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import {
  getWorkflowStatusFallbackLabel,
  isApprovedWorkflowStatus,
  isRejectedWorkflowStatus,
  LEAVE_APPROVAL_STATUS_HISTORY,
  LEAVE_APPROVAL_STATUS_PENDING,
  RoleTypeEnum,
  VIEW_MODE,
  ViewMode,
  WorkflowStatusCode,
} from "../../utils/constants";
import {
  useDecideLeaveMutation,
  useGetLeavesQuery,
} from "../../store/apis/leave.api";
import { SimpleTooltip, PageHeader, ApproveRejectActions, TabSelector } from "../common";
import type { TabItem } from "../common/TabSelector";
import LeaveViewModal, { LeaveViewRecord } from "./LeaveViewModal";

import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { formatDate } from "../../utils/timeUtils";
import { getAvatarGradient } from "../../utils/avatarUtils";

import RejectionModal from "./modals/RejectionModal";

import { useAuth } from "../../store/hooks/useAuth";

import LeaveApprovalSkeleton from "./LeaveApprovalSkeleton";
import type { ILeave } from "../../types/leave.api.types";
import type { ApiError } from "../../store/utils/apiError";

const mapToLeaveViewRecord = (leave: ILeave): LeaveViewRecord => ({
  ...leave,
  appliedDate: leave.createdAt,
  currentApproverName: leave.currentActorName,
  reason: leave.reason || "",
  status: leave.status as LeaveViewRecord["status"],
});

const LeaveApproval: React.FC = () => {
  const [selectedLeave, setSelectedLeave] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"Pending" | "History">("Pending");
  const [viewLeave, setViewLeave] = useState<LeaveViewRecord | null>(null);

  const currentUser = useAuth().user;
  const currentUserId = currentUser?.id;
  const normalizedCurrentUserId = currentUserId ? String(currentUserId) : null;
  const canManageLeaveRequests = useHasPermission(
    PERMISSIONS.LEAVE_REQUESTS_MANAGE
  );
  const canViewAll =
    useHasPermission(PERMISSIONS.LEAVE_REQUESTS_VIEW) || canManageLeaveRequests;

  const isAdmin =
    currentUser?.role?.toLowerCase() === RoleTypeEnum.ADMIN?.toLowerCase() ||
    currentUser?.role?.toLowerCase() ===
    RoleTypeEnum.SUPER_ADMIN?.toLowerCase();

  const [viewMode, setViewMode] = useState<ViewMode>(VIEW_MODE.ALL);

  const [pendingPage, setPendingPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [allPendingCards, setAllPendingCards] = useState<ILeave[]>([]);
  const [allHistoryCards, setAllHistoryCards] = useState<ILeave[]>([]);

  const PAGE_SIZE = 10;

  const pendingParams = useMemo(
    () => ({
      page: pendingPage,
      limit: PAGE_SIZE,
      status: LEAVE_APPROVAL_STATUS_PENDING,
      reporteesOnly: isAdmin && viewMode === VIEW_MODE.REPORTEES,
    }),
    [
      pendingPage,
      isAdmin,
      viewMode,
    ]
  );
  const historyParams = useMemo(
    () => ({
      page: historyPage,
      limit: PAGE_SIZE,
      status: LEAVE_APPROVAL_STATUS_HISTORY,
      reporteesOnly: isAdmin && viewMode === VIEW_MODE.REPORTEES,
    }),
    [
      historyPage,
      isAdmin,
      viewMode,
    ]
  );

  const {
    data: pendingData,
    isLoading: pendingLoading,
    isFetching: pendingFetching,
  } = useGetLeavesQuery(pendingParams, { skip: activeTab !== "Pending" });

  const {
    data: historyData,
    isLoading: historyLoading,
    isFetching: historyFetching,
  } = useGetLeavesQuery(historyParams, { skip: activeTab !== "History" });

  useEffect(() => {
    if (pendingData?.data) {
      setAllPendingCards(prev => {
        if (pendingPage === 1) return pendingData.data;
        const existingIds = new Set(prev.map(c => c.id));
        return [...prev, ...pendingData.data.filter(c => !existingIds.has(c.id))];
      });
    }
  }, [pendingData, pendingPage]);

  useEffect(() => {
    if (historyData?.data) {
      setAllHistoryCards(prev => {
        if (historyPage === 1) return historyData.data;
        const existingIds = new Set(prev.map(c => c.id));
        return [...prev, ...historyData.data.filter(c => !existingIds.has(c.id))];
      });
    }
  }, [historyData, historyPage]);

  const canActOnLeave = useCallback(
    (leave: ILeave) => {
      if (!canManageLeaveRequests) return false;
      if (
        currentUser?.role?.toLowerCase() ===
        RoleTypeEnum.ADMIN?.toLowerCase() ||
        currentUser?.role?.toLowerCase() ===
        RoleTypeEnum.SUPER_ADMIN?.toLowerCase()
      ) {
        return true;
      }
      if (!leave.currentActorIds || !normalizedCurrentUserId) return false;
      return leave.currentActorIds.includes(normalizedCurrentUserId);
    },
    [canManageLeaveRequests, canViewAll, normalizedCurrentUserId, currentUser]
  );

  const [decideLeave, { isLoading: actionLoading }] = useDecideLeaveMutation();

  const handleApprove = async (leaveId: string) => {
    if (!canManageLeaveRequests) {
      toast.error("You do not have permission to approve leave requests.");
      return;
    }
    try {
      await decideLeave({
        id: leaveId,
        body: { decision: "approved" },
      }).unwrap();
      toast.success("Leave approved successfully!");
    } catch (error: unknown) {
      const err = error as ApiError;
      const rawMessage = err?.data?.message || err?.message || "Something went wrong";
      const errorMessage = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;
      toast.error(errorMessage);
    }
  };

  const handleReject = (leaveId: string) => {
    if (!canManageLeaveRequests) {
      toast.error("You do not have permission to reject leave requests.");
      return;
    }
    setSelectedLeave(leaveId);
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!canManageLeaveRequests) {
      toast.error("You do not have permission to reject leave requests.");
      return;
    }
    if (!selectedLeave || !rejectionReason.trim()) return;
    try {
      await decideLeave({
        id: selectedLeave,
        body: { decision: "rejected", remarks: rejectionReason },
      }).unwrap();
      setShowRejectModal(false);
      setRejectionReason("");
      setSelectedLeave(null);
      toast.success("Leave rejected successfully!");
    } catch (error: unknown) {
      const err = error as ApiError;
      const rawMessage = err?.data?.message || err?.message || "Something went wrong";
      const errorMessage = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;
      toast.error(errorMessage);
    }
  };

  const isPending = activeTab === "Pending";
  const isLoading = isPending ? (pendingLoading || pendingFetching) : (historyLoading || historyFetching);
  const currentData = isPending ? pendingData : historyData;
  const currentPage = isPending ? pendingPage : historyPage;
  const setCurrentPage = isPending ? setPendingPage : setHistoryPage;
  const currentCards = isPending ? allPendingCards : allHistoryCards;
  const currentTotal = currentData?.total ?? 0;
  const hasMore = currentCards.length < currentTotal;
  const isFirstLoad = isPending ? (pendingPage === 1 && isLoading) : (historyPage === 1 && isLoading);
  const isFetchingMore = isPending ? (pendingPage > 1 && pendingFetching) : (historyPage > 1 && historyFetching);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    setPendingPage(1);
    setHistoryPage(1);
    setAllPendingCards([]);
    setAllHistoryCards([]);
  };

  return (
    <div className="w-full">

      <PageHeader
        title="Leave Approvals"
        description={
          <span>
            <strong className="text-[var(--color-text-primary)]">{pendingData?.total ?? 0}</strong> requests awaiting your decision
          </span>
        }
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "Leave Approvals" }]}
        actions={
          <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
            {isAdmin && (
              <TabSelector<ViewMode>
                size="sm"
                tabs={[
                  { k: VIEW_MODE.ALL, label: "All Employees" },
                  { k: VIEW_MODE.REPORTEES, label: "My Reportees" },
                ]}
                active={viewMode}
                onChange={handleViewModeChange}
              />
            )}
            <TabSelector<"Pending" | "History">
              size="md"
              tabs={[
                { k: "Pending", label: "Pending" },
                { k: "History", label: "History" },
              ]}
              active={activeTab}
              onChange={(tab) => setActiveTab(tab)}
            />
          </div>
        }
      />

      {/* ── Card List ── */}
      {isFirstLoad ? (
        <LeaveApprovalSkeleton rows={5} />
      ) : (
        <div className="space-y-4">
          {currentCards.length === 0 ? (
            <div className="rounded-[16px] bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] p-8">
              <p className="text-sm text-[var(--color-text-tertiary)] italic text-center">
                {isPending ? "No pending leaves found" : "No historical leaves found"}
              </p>
            </div>
          ) : (
            currentCards.map((leave) => (
              <div
                key={leave.id}
                className="rounded-[16px] bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] p-5 transition-shadow hover:shadow-[var(--shadow-md)]"
              >
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-[200px]">
                    <div
                      className="w-[48px] h-[48px] rounded-[13px] grid place-items-center text-white font-bold text-sm flex-shrink-0"
                      style={{ background: getAvatarGradient(leave.userName || "") }}
                    >
                      {leave.userName?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[var(--color-text-primary)]">{leave.userName}</p>
                      <p className="text-xs text-[var(--color-text-tertiary)]">
                        {leave.departmentName || ""}
                        {leave.createdAt ? ` · applied ${formatDate(leave.createdAt)}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 min-w-[200px] flex flex-wrap gap-5">
                    <div>
                      <p className="text-[11px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.04em] mb-1">Type</p>
                      <p className="text-sm font-bold text-[var(--color-text-primary)]">{leave.leaveTypeName || leave.leaveType || "-"}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.04em] mb-1">Dates</p>
                      <p className="text-sm font-semibold text-[var(--color-text-secondary)]">
                        {formatDate(leave.startDate)} – {formatDate(leave.endDate)} · {leave.days} day{leave.days !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <p className="text-[11px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.04em] mb-1">Reason</p>
                      <SimpleTooltip label={leave.reason || "—"} side="top">
                        <p className="text-sm text-[var(--color-text-secondary)] truncate cursor-pointer max-w-[220px]">
                          {leave.reason || "—"}
                        </p>
                      </SimpleTooltip>
                    </div>
                  </div>
                  <button
                    onClick={() => setViewLeave(mapToLeaveViewRecord(leave))}
                    className="ml-auto shrink-0 text-[var(--color-text-tertiary)] hover:text-[var(--color-primary-600)] transition-colors"
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>

                {/* ── Bottom Row ── */}
                <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-4 border-t border-[var(--color-border-primary)] w-full">
                  {/* Bottom-Left: Workflow tracking or approval history status */}
                  <div className="text-xs font-semibold text-[var(--color-text-secondary)] flex items-center gap-1.5 min-h-[40px]">
                    {isPending ? (
                      <>
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                        <span>
                          {leave.statusLabel ||
                            getWorkflowStatusFallbackLabel(
                              leave.status,
                              leave.currentActorName
                            )}
                        </span>
                      </>
                    ) : (
                      <span>
                        {isApprovedWorkflowStatus(leave.status as WorkflowStatusCode) ? (
                          leave.approvedByName ? (
                            `Approved by ${leave.approvedByName}${leave.approvedDate ? ` on ${formatDate(leave.approvedDate)}` : ""}`
                          ) : (
                            "Approved"
                          )
                        ) : isRejectedWorkflowStatus(leave.status as WorkflowStatusCode) ? (
                          leave.rejectionReason ? (
                            <SimpleTooltip label={`Reason: ${leave.rejectionReason}`} side="top">
                              <span className="cursor-help underline decoration-dotted">
                                Rejected
                              </span>
                            </SimpleTooltip>
                          ) : (
                            "Rejected"
                          )
                        ) : (
                          leave.statusLabel ||
                          getWorkflowStatusFallbackLabel(
                            leave.status,
                            leave.currentActorName
                          )
                        )}
                      </span>
                    )}
                  </div>

                  {/* Bottom-Right: Action buttons or final status badge */}
                  <div className="flex items-center gap-2">
                    {isPending && canActOnLeave(leave) && canManageLeaveRequests && (
                      <ApproveRejectActions
                        onApprove={() => handleApprove(leave.id)}
                        onReject={() => handleReject(leave.id)}
                        loading={actionLoading}
                      />
                    )}

                    {!isPending && (
                      <span
                        className={`inline-flex items-center gap-2 h-10 px-4 rounded-[11px] text-sm font-bold ${
                          isApprovedWorkflowStatus(leave.status as WorkflowStatusCode)
                            ? "bg-emerald-50 text-emerald-600"
                            : isRejectedWorkflowStatus(leave.status as WorkflowStatusCode)
                              ? "bg-red-50 text-red-600"
                              : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {isApprovedWorkflowStatus(leave.status as WorkflowStatusCode) ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        {isApprovedWorkflowStatus(leave.status as WorkflowStatusCode)
                          ? "Approved"
                          : isRejectedWorkflowStatus(leave.status as WorkflowStatusCode)
                            ? "Rejected"
                            : leave.statusLabel ||
                              getWorkflowStatusFallbackLabel(
                                leave.status,
                                leave.currentActorName
                              )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* ── Load More ── */}
          {hasMore && (
            <div className="flex justify-center pt-2 pb-4">
              <button
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={isFetchingMore}
                className="h-10 px-6 rounded-[11px] border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] text-sm font-bold text-[var(--color-text-secondary)] inline-flex items-center gap-2 hover:bg-[var(--color-bg-muted)] transition-all disabled:opacity-50"
              >
                {isFetchingMore ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                {isFetchingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
          {!hasMore && currentCards.length > 0 && (
            <div className="flex justify-center pt-1 pb-2">
              <p className="text-xs text-[var(--color-text-tertiary)]">Showing all {currentTotal} requests</p>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      {showRejectModal && (
        <RejectionModal
          rejectionReason={rejectionReason}
          selectedLeave={selectedLeave}
          confirmReject={confirmReject}
          processing={processing}
          setRejectionReason={setRejectionReason}
          setSelectedLeave={setSelectedLeave}
          setShowRejectModal={setShowRejectModal}
        />
      )}

      <LeaveViewModal record={viewLeave} onClose={() => setViewLeave(null)} />
    </div>
  );
};

export default LeaveApproval;
